import React, {useMemo} from 'react';

import {
  ProcedureParameterUsageValueType,
  ProcedureStepGroupType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {areMaterialOutputsConfirmed} from 'notebook/worksheets/util/materialOutputsConfirmed';
import {
  StepTaskOrInaccessibleGQL,
  Worksheet,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {getStepGroupFromLabel} from 'procedures/ProcedureDomainTypes';
import {PMEIsQuery_procedureMethodExecutionInstances as PMEIOrInaccessible} from 'procedures/worksheets/__generated/PMEIsQuery';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';

export interface OutputConfirmationStep {
  name: string;
  url?: string;
  group: ProcedureStepGroupType;
}

export interface OutputConfirmationTaskData {
  stepsWithUnconfirmedOutputs: Array<OutputConfirmationStep>;
  areConfirmed: boolean;
}

const getOutputConfirmationTaskData = (
  worksheet: Worksheet | null,
  transitionToStatus: WorksheetTaskStatusType,
  pmeis?: Array<PMEIOrInaccessible>
): Array<OutputConfirmationTaskData> =>
  pmeis && pmeis.length > 0
    ? pmeis.map((pmei) => {
        // Material output confirmations are irrelevant for the other transition statuses, so we can shortcircuit
        if (
          ![WorksheetTaskStatusType.IN_PROGRESS, WorksheetTaskStatusType.COMPLETED].includes(
            transitionToStatus
          )
        ) {
          return {areConfirmed: true, stepsWithUnconfirmedOutputs: [] as Array<OutputConfirmationStep>};
        }
        if (pmei == null || pmei.__typename !== 'ProcedureMethodExecutionInstanceNode') {
          return {areConfirmed: false, stepsWithUnconfirmedOutputs: []};
        }

        if (isInaccessible(pmei.planMethodDefinitionVersion)) {
          return {areConfirmed: false, stepsWithUnconfirmedOutputs: []};
        }

        const {areConfirmed, unconfirmedIds: unconfirmedMaterialOutputIds} =
          areMaterialOutputsConfirmed(pmei);

        if (areConfirmed || pmei == null) {
          return {areConfirmed, stepsWithUnconfirmedOutputs: [] as Array<OutputConfirmationStep>};
        }

        // Get PSDV ids for all steps containing confirmation tables for the unconfirmed material outputs
        const materialOutputConfirmationUsages =
          pmei.planMethodDefinitionVersion.parameterUsagesExpensive.filter(
            (usage) =>
              unconfirmedMaterialOutputIds.includes(usage.parameterVersionId) &&
              usage.valueType === ProcedureParameterUsageValueType.CONFIRMATION
          );
        const materialOutputConfirmationUsageStepIds = materialOutputConfirmationUsages.map(
          (usage) => usage.stepId
        );
        const explicitlyConfirmedMaterialOutputIds = materialOutputConfirmationUsages.map(
          (usage) => usage.parameterVersionId
        );
        const megastepUnconfirmedMaterialOutputIds = unconfirmedMaterialOutputIds.filter(
          (outputId) => !explicitlyConfirmedMaterialOutputIds.includes(outputId)
        );

        const stepGroups = worksheet?.worksheetStepGroups ?? [];
        const getStepVersionId = (task: StepTaskOrInaccessibleGQL): string | null => {
          if (
            task?.__typename === 'ProcessTaskNode' &&
            task.procedureStepExecutionInstance?.__typename === 'ProcedureStepExecutionInstanceNode'
          ) {
            return task.procedureStepExecutionInstance.planStepVersion?.id ?? null;
          }
          return null;
        };
        // Construct list of actual worksheet steps with document name & urls corresponding to the PSDVs with
        // unconfirmed outputs
        const stepsWithUnconfirmedOutputs: Array<OutputConfirmationStep> = stepGroups.flatMap((group) =>
          group.worksheetSteps
            .filter(
              (step) =>
                step.tasks.filter((task) => {
                  const versionId = getStepVersionId(task);
                  return versionId != null && materialOutputConfirmationUsageStepIds.includes(versionId);
                }).length > 0
            )
            .map((step) => ({
              group: getStepGroupFromLabel(group.name),
              name: step.document.name,
              url: step.document.editUrl,
            }))
        );
        // The material output megastep doesn't have a PSDV, so we need to add it on explicitly if there are
        // unconfirmed material outputs there
        if (pmei.materialOutputConfirmationStep && megastepUnconfirmedMaterialOutputIds.length > 0) {
          stepsWithUnconfirmedOutputs.push({
            group: ProcedureStepGroupType.RUN,
            name: pmei.materialOutputConfirmationStep.name,
            url: !isInaccessible(pmei.materialOutputConfirmationStep.document)
              ? pmei.materialOutputConfirmationStep.document.editUrl
              : undefined,
          });
        }
        return {stepsWithUnconfirmedOutputs, areConfirmed};
      })
    : [{areConfirmed: false, stepsWithUnconfirmedOutputs: []}];

/**
 * Check for unconfirmed material outputs & return error banner linking out to steps with unconfirmed outputs.
 *
 * If parameter tables cannot be inserted into steps, we only get an error on completing a run & reference the
 * material outputs megastep. If they can be inserted, then confirmations could also be in any pre-run or run
 * steps, so need to be referenced more explicitly.
 *
 * @param worksheet Worksheet with all info to compute errors from.
 * @param transitionToStatus Status user is attempting to transition to. Output confirmation errors are only
 * relevant for starting & completing a run (IN_PROGRESS & COMPLETED statuses)
 * @return JSX.Element optional error banner to render explaining the error & linking to steps with output
 * confirmations.
 */
export function useMaterialOutputsConfirmationErrorBanner({
  worksheet,
  transitionToStatus,
  pmeis,
}: {
  worksheet: Worksheet | null;
  transitionToStatus: WorksheetTaskStatusType;
  pmeis?: Array<PMEIOrInaccessible>;
}): JSX.Element | null {
  const outputConfirmationTaskData = useMemo(
    () => getOutputConfirmationTaskData(worksheet, transitionToStatus, pmeis),
    [worksheet, transitionToStatus, pmeis]
  );

  // We put all steps with unconfirmed outputs in a map keyed by name and url, and then use its values to
  // to de-dupe links to the same step. This can happen if different PMEIs report unsubmitted material outputs
  // for the same step.
  const allStepsWithUnconfirmedOutputs: {[key: string]: OutputConfirmationStep} = outputConfirmationTaskData
    .flatMap((taskData) => taskData.stepsWithUnconfirmedOutputs)
    .reduce((acc: {[key: string]: OutputConfirmationStep}, step) => {
      acc[`${step.name}-${step.url}`] = step;
      return acc;
    }, {});

  const areAllConfirmed = outputConfirmationTaskData.every((taskData) => taskData.areConfirmed);

  // If we're transitioning to IN_PROGRESS, we only care about outputs in pre-run steps. Otherwise, we need
  // to consider all unconfirmed material outputs.
  const relevantConfirmationSteps =
    transitionToStatus === WorksheetTaskStatusType.IN_PROGRESS
      ? Object.values(allStepsWithUnconfirmedOutputs).filter(
          (step) => step.group === ProcedureStepGroupType.PRE_RUN
        )
      : Object.values(allStepsWithUnconfirmedOutputs);

  const hasMaterialOutputErrors = ((): boolean => {
    // For in progress, we only block if there are pre-run steps with unconfirmed material outputs.
    if (transitionToStatus === WorksheetTaskStatusType.IN_PROGRESS) {
      return relevantConfirmationSteps.length > 0;
    }
    // For consistency, this also returns true when the PMEI is missing/inaccessible (& thus there are no steps)
    return !areAllConfirmed;
  })();
  return hasMaterialOutputErrors ? (
    <MaterialOutputsConfirmationErrorBanner
      transitionToStatus={transitionToStatus}
      outputConfirmationSteps={relevantConfirmationSteps}
    />
  ) : null;
}

export function MaterialOutputsConfirmationErrorBanner({
  transitionToStatus,
  outputConfirmationSteps,
}: {
  transitionToStatus: WorksheetTaskStatusType;
  outputConfirmationSteps: Array<OutputConfirmationStep>;
}): JSX.Element | null {
  const errorMessage =
    transitionToStatus === WorksheetTaskStatusType.IN_PROGRESS
      ? "Some material outputs aren't confirmed in your Pre-run steps. Go to the following steps to confirm."
      : "Run can't be completed before all material outputs are confirmed. Go to the following steps to confirm.";

  if (outputConfirmationSteps.length > 0) {
    return (
      <InlineBanner
        bannerType={
          transitionToStatus === WorksheetTaskStatusType.COMPLETED
            ? InlineBannerType.ERROR
            : InlineBannerType.WARNING
        }
        text={errorMessage}
        action={{
          actionType: InlineBannerActionType.ACCORDION,
          rightAlign: true,
          accordionLabels: {moreLabel: 'See more', lessLabel: 'See less'},
          initialShowMore: outputConfirmationSteps.length <= 5,
        }}
        supportingInfo={
          <ul className="u-padding-left">
            {outputConfirmationSteps.map((step, idx) => (
              <li key={`${step.name}-${idx}`}>
                {step.url != null ? (
                  <a target="blank" rel="noopener noreferrer" href={step.url}>
                    {step.name}
                  </a>
                ) : (
                  step.name
                )}
              </li>
            ))}
          </ul>
        }
      />
    );
  }
  return null;
}
